import { PAID_PLAN_IDS, type PaidPlanId } from "./plans.js";

/**
 * The names mailhand gives its Stripe objects. The setup script creates the
 * objects with these names; the API and housekeeping find them by the same names.
 *
 * Each paid subscription has two items: the plan's licensed base price and the
 * one overage price that every paid plan shares. mailhand counts the allowance
 * itself and reports only the overage, in overage units, on one meter.
 */

export const OVERAGE_METER = { eventName: "hooky_overage", displayName: "Hooky overage" } as const;
export const OVERAGE_PRICE_LOOKUP_KEY = "hooky_overage";
export const OVERAGE_PRODUCT_NAME = "Hooky usage";

/** The meter event payload keys. They are Stripe's defaults. */
export const METER_CUSTOMER_KEY = "stripe_customer_id";
export const METER_VALUE_KEY = "value";

export function basePriceLookupKey(plan: PaidPlanId): string {
  return `hooky_${plan}_base`;
}

/** The plan of a base price, from its lookup key. */
export function planFromBaseLookupKey(lookupKey: string): PaidPlanId | undefined {
  return PAID_PLAN_IDS.find((plan) => basePriceLookupKey(plan) === lookupKey);
}

/** Checkout puts this on the subscription's metadata, so every subscription event names its tenant. The customer has it too. */
export const TENANT_METADATA_KEY = "tenantId";

/** Marks the customer portal configuration that the setup script owns. */
export const PORTAL_METADATA = { key: "hooky", value: "portal" } as const;

export const WEBHOOK_EVENTS = [
  "checkout.session.completed",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENTS)[number];

/** Statuses that keep the paid plan. */
export const PAYING_STATUSES = ["active", "trialing", "past_due"] as const;
export type PayingStatus = (typeof PAYING_STATUSES)[number];

/** Statuses of a subscription that still exists but does not pay: the tenant is on free limits until it is paid or canceled. */
export const PAYMENT_PROBLEM_STATUSES = ["unpaid", "incomplete", "paused"] as const;
export type PaymentProblemStatus = (typeof PAYMENT_PROBLEM_STATUSES)[number];

/** Statuses after which the subscription cannot become active again. */
export const ENDED_STATUSES = ["canceled", "incomplete_expired"] as const;
export type EndedStatus = (typeof ENDED_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = [...PAYING_STATUSES, ...PAYMENT_PROBLEM_STATUSES, ...ENDED_STATUSES] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export function isPayingStatus(status: SubscriptionStatus): status is PayingStatus {
  return PAYING_STATUSES.some((paying) => paying === status);
}

export function isEndedStatus(status: SubscriptionStatus): status is EndedStatus {
  return ENDED_STATUSES.some((ended) => ended === status);
}

/** Stripe's `unit_amount_decimal` is in cents: 1,000 micros is `"0.1"`. */
export function microsToCentsDecimal(micros: number): string {
  return String(micros / 10_000);
}

export function usdToCents(usd: number): number {
  return usd * 100;
}
